import React, { useState, useMemo } from 'react';
import { Col, Row, getItemWithExpiration } from '../../lib/utils';
import { colors } from '../../lib/colors';
import { CaptionLight, Subtitle1Bold, Body2Regular } from '../../lib/font';
import Grid, { GridColProps } from '../../components/Grid/Grid';
import RadiusTextInput from '../../components/TextInput/RadiusTextInput';
import SubscriptionEditModal from '../../components/FacilitySubscription/SubscriptionEditModal';
import SubscriptionTransferModal from '../../components/FacilitySubscription/SubscriptionTransferModal';
import { TransferKindBadge } from '../../components/FacilitySubscription/SubscriptionTransferHistory';
import { buildTransferDoneMessage, isCenterViewerAccount } from '@/features/facilitySubscription/transfer';
import { useCenterSubscriptionSummary, useCenterSubscriptions } from '@/features/facilitySubscription/hooks';
import { IFacilitySubscription, SubscriptionDateBase, SubscriptionStatusFilter } from '@/features/facilitySubscription/types';
import dayjs from 'dayjs';
import BusinessInfoFooter from '../../components/BusinessInfoFooter';
import { formatKoreanPhoneNumber } from '../../utils/phoneNumber';
import TextSelector from '../../components/TextSelector';
import { TooltipWrapper } from '../../components/TooltipWrapper';
import { DemiLightText } from '../../lib/Text';

type StatusFilterCode = SubscriptionStatusFilter;

const LIST_LIMIT = 10000;

const STATUS_OPTIONS: { code: StatusFilterCode; value: string }[] = [
    { code: 'all', value: '전체' },
    { code: 'active', value: '이용중' },
    { code: 'paused', value: '일시정지' },
    { code: 'expiringSoon', value: '만료 임박' },
    { code: 'expired', value: '만료' },
    { code: 'refunded', value: '환불' },
];

const PAYMENT_LABEL: Record<string, string> = {
    card: '카드',
    cash: '현금',
    transfer: '이체',
};

const CATEGORY_CONFIG: Record<string, { label: string; icon: string; bg: string; color: string }> = {
    gym: { label: '헬스(GYM)', icon: '🏋️', bg: '#EFF6FF', color: '#1E40AF' },
    locker: { label: '락커', icon: '🔐', bg: '#F5F3FF', color: '#5B21B6' },
    clothing: { label: '운동복', icon: '👕', bg: '#FFFBEB', color: '#92400E' },
};

const STATUS_CFG: Record<string, { label: string; bg: string; color: string; dot: string }> = {
    active: { label: '이용중', bg: '#D1FAE5', color: '#065F46', dot: '#10B981' },
    paused: { label: '일시정지', bg: '#EEF2F6', color: '#475569', dot: '#94A3B8' },
    expiringSoon: {
        label: '만료 임박',
        bg: '#FFFBEB',
        color: '#92400E',
        dot: '#F59E0B',
    },
    expired: { label: '만료', bg: '#F3F4F6', color: '#6B7280', dot: '#9CA3AF' },
    refunded: { label: '환불', bg: '#FEE2E2', color: '#DC2626', dot: '#EF4444' },
};

const SubscriptionListPage = ({ createAlert }: { createAlert: any }) => {
    const [authObject] = useState(getItemWithExpiration('authObject'));
    const centerId = authObject?.centerId;
    const isCenterViewer = isCenterViewerAccount(authObject);

    const [statusFilter, setStatusFilter] = useState<StatusFilterCode>('all');
    const [categoryFilter, setCategoryFilter] = useState<string>('all');
    const [dateBase, setDateBase] = useState<SubscriptionDateBase>('payment');
    const [dateFrom, setDateFrom] = useState<string>('');
    const [dateTo, setDateTo] = useState<string>('');
    const [searchKeyword, setSearchKeyword] = useState<string>('');
    const [viewMode, setViewMode] = useState<'detail' | 'revenue'>('detail');
    const [editTarget, setEditTarget] = useState<IFacilitySubscription | null>(null);
    const [transferTarget, setTransferTarget] = useState<IFacilitySubscription | null>(null);
    const [showGuide, setShowGuide] = useState(() => localStorage.getItem('guide_subscription') !== 'hidden');

    const { data: subscriptionData, isLoading: isSubscriptionLoading } = useCenterSubscriptions({
        centerId,
        status: statusFilter,
        category: categoryFilter,
        search: searchKeyword,
        dateBase,
        dateFrom,
        dateTo,
        limit: LIST_LIMIT,
    });
    const { data: subscriptionSummary, isLoading: isSummaryLoading } = useCenterSubscriptionSummary({
        centerId,
        status: statusFilter,
        category: categoryFilter,
        search: searchKeyword,
        dateBase,
        dateFrom,
        dateTo,
    });

    const subscriptions = useMemo(() => {
        const mapped = (subscriptionData?.results ?? []).map((sub: any) => ({
            ...sub,
            memberName: sub.memberName || '-',
            phoneNumber: formatKoreanPhoneNumber(sub.phoneNumber, '-'),
        }));
        const dateField = dateBase === 'payment' ? 'paymentDate' : dateBase === 'end' ? 'endDate' : 'startDate';
        return [...mapped].sort((a: any, b: any) => {
            const aVal = a[dateField];
            const bVal = b[dateField];
            if (!aVal && !bVal) return 0;
            if (!aVal) return 1;
            if (!bVal) return -1;
            return bVal.localeCompare(aVal);
        });
    }, [subscriptionData, dateBase]);
    const totalResults = subscriptionSummary?.totalResults ?? subscriptionData?.totalResults ?? subscriptions.length;

    const fallbackRevenue = useMemo(() => {
        const gross = subscriptions.reduce((sum: number, s: any) => sum + (s.price || 0), 0);
        const refundTotal = subscriptions
            .filter((s: any) => s.status === 'refunded')
            .reduce((sum: number, s: any) => sum + (s.refundAmount ?? s.price ?? 0), 0);
        const net = gross - refundTotal;
        const refundCount = subscriptions.filter((s: any) => s.status === 'refunded').length;

        // 상품별 집계 — 순매출 기준 (price 합 - refundAmount 합)
        const productMap: Record<
            string,
            {
                name: string;
                count: number;
                refundCount: number;
                gross: number;
                refund: number;
            }
        > = {};
        subscriptions.forEach((s: any) => {
            const name = s.productName || '기타';
            if (!productMap[name])
                productMap[name] = {
                    name,
                    count: 0,
                    refundCount: 0,
                    gross: 0,
                    refund: 0,
                };
            productMap[name].count++;
            productMap[name].gross += s.price || 0;
            if (s.status === 'refunded') {
                productMap[name].refundCount++;
                productMap[name].refund += s.refundAmount ?? s.price ?? 0;
            }
        });
        const products = Object.values(productMap)
            .map((p) => ({ ...p, net: p.gross - p.refund }))
            .sort((a, b) => b.net - a.net);

        return {
            gross,
            refundTotal,
            net,
            count: subscriptions.length,
            refundCount,
            products,
        };
    }, [subscriptions]);
    const revenue = subscriptionSummary?.revenue ?? fallbackRevenue;

    const fallbackKpi = useMemo(() => {
        const isPausedRow = (s: any) => s.displayStatus === 'paused';
        return {
            // 이용중 = 정지 아닌 active
            active: subscriptions.filter((s: any) => s.status === 'active' && !isPausedRow(s)).length,
            paused: subscriptions.filter(isPausedRow).length,
            expired: subscriptions.filter((s: any) => s.status === 'expired').length,
            refunded: subscriptions.filter((s: any) => s.status === 'refunded').length,
            expiringSoon: subscriptions.filter((s: any) => {
                if (s.status !== 'active' || isPausedRow(s) || !s.endDate) return false;
                const daysLeft = dayjs(s.endDate).diff(dayjs(), 'day');
                return daysLeft >= 0 && daysLeft <= 7;
            }).length,
        };
    }, [subscriptions]);
    const kpi = subscriptionSummary?.statusCounts ?? fallbackKpi;
    const isPageLoading = isSubscriptionLoading || isSummaryLoading;
    const searchInput = (
        <div style={{ width: 310, height: 36 }}>
            <RadiusTextInput
                value={searchKeyword}
                onChange={(value) => setSearchKeyword(value)}
                clearable
                onClear={() => setSearchKeyword('')}
                placeholder="회원명, 연락처, 상품명"
                width={310}
                height={36}
                isRightIcon
            />
        </div>
    );

    const colDefs: GridColProps[] = [
        {
            field: 'memberName',
            headerName: '회원명',
            // 배지('회원 연결 끊김')가 셀 좌우 패딩(px-6, 48px) 안에 들어가도록 110 → 140.
            width: 140,
            align: 'center',
            nullValue: '-',
            customComponent: (value, row) => {
                const nameText = value || '-';
                // memberLinkBroken 이 없는(구버전) 응답에서는 undefined → 기존과 동일하게 이름만 표시.
                if (row.memberLinkBroken !== true) {
                    return <TextSelector text={nameText} className="text-LAVEL_3" fontType="Body3Regular" />;
                }
                return (
                    <Col className="items-center gap-[2px]">
                        <TextSelector text={nameText} className="text-LAVEL_3" fontType="Body3Regular" />
                        <TooltipWrapper
                            tooltip={{
                                opacity: 1,
                                width: 250,
                                customContent: (
                                    <DemiLightText fontSize={13} lineHeight={18} color={colors.white}>
                                        회원 정보가 삭제되어 회원 페이지에서 조회되지 않습니다.
                                    </DemiLightText>
                                ),
                            }}
                        >
                            <span className="px-[5px] py-[1px] rounded-[4px] bg-[#FEF2F2] text-[#B91C1C] text-[10px] font-bold border border-[#FECACA] whitespace-nowrap">
                                회원 연결 끊김
                            </span>
                        </TooltipWrapper>
                    </Col>
                );
            },
        },
        {
            field: 'phoneNumber',
            headerName: '연락처',
            width: 140,
            align: 'center',
            nullValue: '-',
        },
        { field: 'productName', headerName: '상품명', width: 160, align: 'center' },
        {
            field: 'lockerNumber',
            headerName: '라커번호',
            width: 100,
            align: 'center',
            customComponent: (value, row) => {
                const displayLockerNumber = row.displayLockerNumber || value;
                return displayLockerNumber ? (
                    <span className="px-[6px] py-[2px] rounded-[4px] bg-[#F5F3FF] text-[#5B21B6] text-[12px] font-semibold">{displayLockerNumber}</span>
                ) : (
                    <span className="text-[13px] text-[#D1D5DB]">-</span>
                );
            },
        },
        {
            field: 'category',
            headerName: '카테고리',
            width: 110,
            align: 'center',
            customComponent: (value) => {
                const cfg = CATEGORY_CONFIG[value] || {
                    label: value || '-',
                    icon: '📦',
                    bg: '#F3F4F6',
                    color: '#374151',
                };
                return (
                    <span
                        className="inline-flex items-center gap-[4px] px-[8px] py-[3px] rounded-[6px] text-[12px] font-medium"
                        style={{ backgroundColor: cfg.bg, color: cfg.color }}
                    >
                        {cfg.icon} {cfg.label}
                    </span>
                );
            },
        },
        {
            field: 'startDate',
            headerName: '시작일',
            width: 100,
            align: 'center',
            customComponent: (value) => <span className="text-[13px] text-[#374151]">{value ? dayjs(value).format('YY.MM.DD') : '-'}</span>,
        },
        {
            field: 'endDate',
            headerName: '종료일',
            width: 120,
            align: 'center',
            customComponent: (value, row) => {
                if (!value) return <span className="text-[13px] text-[#9CA3AF]">-</span>;
                const formatted = dayjs(value).format('YY.MM.DD');
                // 정지 중에는 종료일이 아직 연장 전이라 D-day 대신 '일시정지 중'으로 표시한다.
                if (row.displayStatus === 'paused') {
                    return (
                        <Col className="items-center gap-[2px]">
                            <span className="text-[13px] text-[#94A3B8]">{formatted}</span>
                            <span className="px-[5px] py-[1px] rounded-[4px] bg-[#EEF2F6] text-[#475569] text-[10px] font-bold border border-[#CBD5E1]">
                                일시정지 중
                            </span>
                        </Col>
                    );
                }
                if (row.status !== 'active') {
                    return <span className="text-[13px] text-[#9CA3AF]">{formatted}</span>;
                }
                const daysLeft = dayjs(value).diff(dayjs(), 'day');
                if (daysLeft < 0) {
                    return <span className="text-[13px] text-[#DC2626]">{formatted}</span>;
                }
                if (daysLeft <= 7) {
                    return (
                        <Col className="items-center gap-[2px]">
                            <span className="text-[13px] text-[#EA580C] font-medium">{formatted}</span>
                            <span className="px-[5px] py-[1px] rounded-[4px] bg-[#FFF7ED] text-[#EA580C] text-[10px] font-bold border border-[#FED7AA]">
                                D-{daysLeft}
                            </span>
                        </Col>
                    );
                }
                return <span className="text-[13px] text-[#374151]">{formatted}</span>;
            },
        },
        {
            field: 'price',
            headerName: '금액',
            width: 110,
            align: 'center',
            customComponent: (value) => <span className="text-[13px] font-semibold text-[#1E40AF]">{Number(value).toLocaleString()}원</span>,
        },
        {
            field: 'paymentMethod',
            headerName: '결제',
            width: 70,
            align: 'center',
            customComponent: (value) => {
                const label = PAYMENT_LABEL[value];
                if (!label) return <span className="text-[13px] text-[#9CA3AF]">-</span>;
                const cfg: Record<string, { bg: string; color: string }> = {
                    card: { bg: '#EFF6FF', color: '#1D4ED8' },
                    cash: { bg: '#F0FDF4', color: '#166534' },
                    transfer: { bg: '#FFF7ED', color: '#92400E' },
                };
                const c = cfg[value] || { bg: '#F3F4F6', color: '#374151' };
                return (
                    <span className="px-[6px] py-[2px] rounded-[4px] text-[11px] font-medium" style={{ backgroundColor: c.bg, color: c.color }}>
                        {label}
                    </span>
                );
            },
        },
        {
            field: 'status',
            headerName: '상태',
            width: 90,
            align: 'center',
            customComponent: (value, row) => {
                // displayStatus가 있으면 우선(정지 중이면 'paused')
                const key = row.displayStatus || value;
                const c = STATUS_CFG[key] || {
                    label: key,
                    bg: '#F3F4F6',
                    color: '#6B7280',
                    dot: '#9CA3AF',
                };
                const hasScheduledPause = Boolean(row.pauseSummary?.scheduledPause);
                return (
                    <Col className="items-center gap-[2px]">
                        <span
                            className="inline-flex items-center gap-[4px] px-[8px] py-[3px] rounded-[6px] text-[11px] font-semibold"
                            style={{ backgroundColor: c.bg, color: c.color }}
                        >
                            <span
                                style={{
                                    width: 6,
                                    height: 6,
                                    borderRadius: '50%',
                                    backgroundColor: c.dot,
                                    display: 'inline-block',
                                    flexShrink: 0,
                                }}
                            />
                            {c.label}
                        </span>
                        <TransferKindBadge kind={row.transferKind} size="sm" />
                        {hasScheduledPause && (
                            <span className="px-[5px] py-[1px] rounded-[4px] bg-[#F1F5F9] text-[#64748B] text-[10px] font-bold border border-[#CBD5E1]">
                                정지예정
                            </span>
                        )}
                    </Col>
                );
            },
        },
    ];

    return (
        <Col
            style={{
                width: '100%',
                height: '100%',
                backgroundColor: '#F8FAFC',
                marginTop: 64,
            }}
        >
            <Col className="w-full px-[48px] pt-[32px] pb-[20px]">
                {/* 헤더 */}
                <Row className="items-center justify-between">
                    <Col>
                        <Row className="items-center gap-[8px]">
                            <Subtitle1Bold>이용권 현황 · 매출</Subtitle1Bold>
                            {!showGuide && (
                                <button
                                    onClick={() => {
                                        setShowGuide(true);
                                        localStorage.removeItem('guide_subscription');
                                    }}
                                    style={{
                                        width: 22,
                                        height: 22,
                                        borderRadius: '50%',
                                        border: '1.5px solid #6EE7B7',
                                        backgroundColor: '#F0FDF4',
                                        color: '#059669',
                                        fontSize: 12,
                                        fontWeight: 700,
                                        cursor: 'pointer',
                                        lineHeight: 1,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                >
                                    ?
                                </button>
                            )}
                        </Row>
                        <CaptionLight color={colors.LAVEL_1} className="mt-[4px]">
                            시설 이용권의 현황과 매출을 한 화면에서 관리합니다
                        </CaptionLight>
                    </Col>
                    {/* 뷰 모드 토글 */}
                    <Row className="border border-[#E1E4E6] rounded-[8px] overflow-hidden" style={{ height: 36 }}>
                        {[
                            { label: '상세', value: 'detail' as const },
                            { label: '매출 요약', value: 'revenue' as const },
                        ].map((tab, i, arr) => (
                            <button
                                key={tab.value}
                                type="button"
                                onClick={() => setViewMode(tab.value)}
                                style={{
                                    padding: '0 16px',
                                    fontSize: 13,
                                    fontWeight: viewMode === tab.value ? 700 : 400,
                                    backgroundColor: viewMode === tab.value ? '#EFF6FF' : 'white',
                                    color: viewMode === tab.value ? '#1D4ED8' : '#6B7280',
                                    border: 'none',
                                    borderRight: i < arr.length - 1 ? '1px solid #E1E4E6' : 'none',
                                    cursor: 'pointer',
                                    whiteSpace: 'nowrap',
                                    transition: 'all 0.15s',
                                }}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </Row>
                </Row>

                {/* 안내 배너 */}
                {showGuide && (
                    <Col
                        style={{
                            marginTop: 16,
                            padding: '20px 24px',
                            backgroundColor: '#F0FDF4',
                            borderRadius: 12,
                            border: '1px solid #A7F3D0',
                            position: 'relative',
                        }}
                    >
                        <button
                            onClick={() => {
                                setShowGuide(false);
                                localStorage.setItem('guide_subscription', 'hidden');
                            }}
                            style={{
                                position: 'absolute',
                                top: 12,
                                right: 16,
                                background: 'none',
                                border: 'none',
                                fontSize: 16,
                                color: '#6EE7B7',
                                cursor: 'pointer',
                            }}
                        >
                            ✕
                        </button>
                        <span
                            style={{
                                fontSize: 14,
                                fontWeight: 700,
                                color: '#065F46',
                                marginBottom: 8,
                            }}
                        >
                            이용권 현황이란?
                        </span>
                        <Col
                            style={{
                                gap: 4,
                                fontSize: 13,
                                color: '#065F46',
                                lineHeight: 1.7,
                            }}
                        >
                            <span>
                                회원에게 발급된 <b>헬스, 락커, 운동복</b> 등 시설 이용권의 현황을 한눈에 확인하는 페이지입니다.
                            </span>
                            <span>
                                락커 필터는 <b>판매된 락커 이용권</b> 기준이며, 실제 점유/미회수 상태는 락커 관리에서 확인합니다.
                            </span>
                            <span style={{ color: '#7C3AED' }}>
                                GX 수업 수강권은 <b>'그룹 수강권 현황'</b>에서 관리됩니다.
                            </span>
                        </Col>
                        <Row style={{ gap: 6, marginTop: 12, flexWrap: 'wrap' }}>
                            <span
                                style={{
                                    fontSize: 12,
                                    padding: '3px 10px',
                                    borderRadius: 6,
                                    backgroundColor: '#D1FAE5',
                                    color: '#065F46',
                                }}
                            >
                                회원 상세에서 이용권 발급
                            </span>
                            <span style={{ fontSize: 12, color: '#6B7280' }}>→</span>
                            <span
                                style={{
                                    fontSize: 12,
                                    padding: '3px 10px',
                                    borderRadius: 6,
                                    backgroundColor: '#D1FAE5',
                                    color: '#065F46',
                                }}
                            >
                                이곳에서 현황 확인/환불
                            </span>
                            <span style={{ fontSize: 12, color: '#6B7280' }}>|</span>
                            <span
                                style={{
                                    fontSize: 12,
                                    padding: '3px 10px',
                                    borderRadius: 6,
                                    backgroundColor: '#EDE9FE',
                                    color: '#5B21B6',
                                }}
                            >
                                🔐 락커 필터로 배정 확인
                            </span>
                        </Row>
                    </Col>
                )}

                {/* KPI 카드 */}
                {!isPageLoading && (
                    <Row className="mt-[20px] gap-[12px]">
                        {(
                            [
                                {
                                    label: '필터 결과',
                                    value: totalResults,
                                    suffix: '건',
                                    bg: '#EEF2FF',
                                    border: '#C7D2FE',
                                    numColor: '#4338CA',
                                    labelColor: '#6366F1',
                                    barColor: '#6366F1',
                                },
                                {
                                    label: '이용중',
                                    value: kpi.active,
                                    suffix: '건',
                                    bg: '#ECFDF5',
                                    border: '#A7F3D0',
                                    numColor: '#065F46',
                                    labelColor: '#059669',
                                    barColor: '#10B981',
                                },
                                ...((kpi.paused ?? 0) > 0
                                    ? [
                                          {
                                              label: '일시정지',
                                              value: kpi.paused,
                                              suffix: '건',
                                              bg: '#F1F5F9',
                                              border: '#CBD5E1',
                                              numColor: '#475569',
                                              labelColor: '#64748B',
                                              barColor: '#94A3B8',
                                          },
                                      ]
                                    : []),
                                {
                                    label: '만료',
                                    value: kpi.expired,
                                    suffix: '건',
                                    bg: '#F9FAFB',
                                    border: '#E5E7EB',
                                    numColor: '#374151',
                                    labelColor: '#6B7280',
                                    barColor: '#9CA3AF',
                                },
                                {
                                    label: '환불',
                                    value: kpi.refunded,
                                    suffix: '건',
                                    bg: '#FEF2F2',
                                    border: '#FECACA',
                                    numColor: '#991B1B',
                                    labelColor: '#DC2626',
                                    barColor: '#EF4444',
                                },
                                ...(kpi.expiringSoon > 0
                                    ? [
                                          {
                                              label: '만료 임박',
                                              value: kpi.expiringSoon,
                                              suffix: '건',
                                              bg: '#FFFBEB',
                                              border: '#FDE68A',
                                              numColor: '#92400E',
                                              labelColor: '#D97706',
                                              barColor: '#F59E0B',
                                          },
                                      ]
                                    : []),
                                {
                                    label: '순매출 (환불 차감)',
                                    value: revenue.net,
                                    suffix: '원',
                                    formatted: true,
                                    bg: '#EFF6FF',
                                    border: '#BFDBFE',
                                    numColor: '#1E40AF',
                                    labelColor: '#2563EB',
                                    barColor: '#3B82F6',
                                },
                            ] as {
                                label: string;
                                value: number;
                                suffix: string;
                                formatted?: boolean;
                                bg: string;
                                border: string;
                                numColor: string;
                                labelColor: string;
                                barColor: string;
                            }[]
                        ).map((item) => (
                            <Col
                                key={item.label}
                                className="flex-1 rounded-[12px] px-[20px] py-[16px] relative overflow-hidden"
                                style={{
                                    backgroundColor: item.bg,
                                    border: `1px solid ${item.border}`,
                                    minWidth: 110,
                                }}
                            >
                                {/* 왼쪽 컬러 바 */}
                                <div
                                    style={{
                                        position: 'absolute',
                                        left: 0,
                                        top: 0,
                                        bottom: 0,
                                        width: 4,
                                        backgroundColor: item.barColor,
                                        borderRadius: '12px 0 0 12px',
                                    }}
                                />
                                <span
                                    style={{
                                        fontSize: 11,
                                        fontWeight: 600,
                                        color: item.labelColor,
                                        marginBottom: 4,
                                    }}
                                >
                                    {item.label}
                                </span>
                                <span
                                    style={{
                                        fontSize: item.formatted ? 22 : 28,
                                        fontWeight: 800,
                                        color: item.numColor,
                                        lineHeight: 1,
                                    }}
                                >
                                    {item.formatted ? item.value.toLocaleString() : item.value}
                                    <span style={{ fontSize: 13, fontWeight: 500, marginLeft: 2 }}>{item.suffix}</span>
                                </span>
                            </Col>
                        ))}
                    </Row>
                )}

                {/* 상태 필터 */}
                <Row className="mt-[20px] gap-[8px] items-center" style={{ flexWrap: 'wrap' }}>
                    <Body2Regular color={colors.LAVEL_2} style={{ marginRight: 4, fontSize: 13 }}>
                        상태
                    </Body2Regular>
                    {STATUS_OPTIONS.map((opt) => {
                        const isSelected = statusFilter === opt.code;
                        const cfg = STATUS_CFG[opt.code];
                        return (
                            <button
                                key={opt.code}
                                style={{
                                    padding: '6px 16px',
                                    borderRadius: 20,
                                    fontSize: 13,
                                    fontWeight: 600,
                                    border: isSelected ? `1.5px solid ${cfg?.dot || '#3B82F6'}` : '1px solid #D1D5DB',
                                    backgroundColor: isSelected ? cfg?.bg || '#EFF6FF' : 'white',
                                    color: isSelected ? cfg?.color || '#1E40AF' : '#374151',
                                    cursor: 'pointer',
                                    transition: 'all 0.15s',
                                }}
                                onClick={() => setStatusFilter(opt.code)}
                            >
                                {opt.code !== 'all' && cfg && (
                                    <span
                                        style={{
                                            display: 'inline-block',
                                            width: 6,
                                            height: 6,
                                            borderRadius: '50%',
                                            backgroundColor: isSelected ? cfg.dot : '#9CA3AF',
                                            marginRight: 5,
                                            verticalAlign: 'middle',
                                        }}
                                    />
                                )}
                                {opt.value}
                            </button>
                        );
                    })}
                    <span
                        style={{
                            width: 1,
                            height: 24,
                            backgroundColor: '#D1D5DB',
                            margin: '0 8px',
                        }}
                    />
                    <Body2Regular color={colors.LAVEL_2} style={{ marginRight: 4, fontSize: 13 }}>
                        카테고리
                    </Body2Regular>
                    {[
                        { code: 'all', label: '전체' },
                        ...Object.entries(CATEGORY_CONFIG).map(([code, cfg]) => ({
                            code,
                            label: `${cfg.icon} ${cfg.label}`,
                        })),
                    ].map((opt) => {
                        const isSelected = categoryFilter === opt.code;
                        const cfg = CATEGORY_CONFIG[opt.code];
                        return (
                            <button
                                key={opt.code}
                                style={{
                                    padding: '6px 14px',
                                    borderRadius: 20,
                                    fontSize: 13,
                                    fontWeight: isSelected ? 600 : 400,
                                    border: isSelected ? `1.5px solid ${cfg?.color || '#3B82F6'}` : '1px solid #D1D5DB',
                                    backgroundColor: isSelected ? cfg?.bg || '#EFF6FF' : 'white',
                                    color: isSelected ? cfg?.color || '#1E40AF' : '#374151',
                                    cursor: 'pointer',
                                    transition: 'all 0.15s',
                                }}
                                onClick={() => setCategoryFilter(opt.code)}
                            >
                                {opt.label}
                            </button>
                        );
                    })}
                </Row>

                {/* 날짜 범위 필터 */}
                <Row className="mt-[12px] gap-[8px] items-center" style={{ flexWrap: 'wrap' }}>
                    <Body2Regular color={colors.LAVEL_2} style={{ marginRight: 4, fontSize: 13 }}>
                        날짜 기준
                    </Body2Regular>
                    <Row className="border border-[#D1D5DB] rounded-[8px] overflow-hidden" style={{ height: 32 }}>
                        {[
                            { label: '결제일', value: 'payment' as const },
                            { label: '시작일', value: 'start' as const },
                            { label: '종료일', value: 'end' as const },
                        ].map((tab, i, arr) => (
                            <button
                                key={tab.value}
                                type="button"
                                onClick={() => setDateBase(tab.value)}
                                style={{
                                    padding: '0 12px',
                                    fontSize: 12,
                                    fontWeight: dateBase === tab.value ? 600 : 400,
                                    backgroundColor: dateBase === tab.value ? '#EFF6FF' : 'white',
                                    color: dateBase === tab.value ? '#1D4ED8' : '#6B7280',
                                    border: 'none',
                                    borderRight: i < arr.length - 1 ? '1px solid #D1D5DB' : 'none',
                                    cursor: 'pointer',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </Row>
                    <input
                        type="date"
                        value={dateFrom}
                        onChange={(e) => setDateFrom(e.target.value)}
                        style={{
                            padding: '6px 10px',
                            borderRadius: 8,
                            border: '1px solid #D1D5DB',
                            fontSize: 13,
                            color: '#374151',
                            cursor: 'pointer',
                        }}
                    />
                    <span style={{ fontSize: 13, color: '#6B7280' }}>~</span>
                    <input
                        type="date"
                        value={dateTo}
                        onChange={(e) => setDateTo(e.target.value)}
                        style={{
                            padding: '6px 10px',
                            borderRadius: 8,
                            border: '1px solid #D1D5DB',
                            fontSize: 13,
                            color: '#374151',
                            cursor: 'pointer',
                        }}
                    />
                    {(dateFrom || dateTo) && (
                        <button
                            onClick={() => {
                                setDateFrom('');
                                setDateTo('');
                            }}
                            style={{
                                padding: '6px 12px',
                                borderRadius: 20,
                                fontSize: 12,
                                border: '1px solid #E5E7EB',
                                backgroundColor: '#F9FAFB',
                                color: '#6B7280',
                                cursor: 'pointer',
                            }}
                        >
                            초기화
                        </button>
                    )}
                    <div style={{ marginLeft: 'auto' }}>{searchInput}</div>
                </Row>
            </Col>

            {/* 테이블 */}
            <Col
                style={{
                    width: 'calc(100% - 96px)',
                    margin: '0 48px',
                    backgroundColor: 'white',
                    borderRadius: 12,
                    border: '1px solid #E5E7EB',
                    overflow: 'hidden',
                }}
            >
                {isPageLoading ? (
                    <Col className="items-center justify-center py-[60px] gap-[12px]">
                        <div className="w-[28px] h-[28px] border-[3px] border-[#3B82F6] border-t-transparent rounded-full animate-spin" />
                        <CaptionLight color={colors.LAVEL_1}>이용권 목록을 불러오는 중...</CaptionLight>
                    </Col>
                ) : subscriptions.length === 0 ? (
                    <Col className="items-center justify-center py-[60px] gap-[12px]">
                        <span className="text-[40px]">🎫</span>
                        <Body2Regular color={colors.LAVEL_2}>
                            {statusFilter !== 'all'
                                ? `'${STATUS_OPTIONS.find((o) => o.code === statusFilter)?.value}' 이용권이 없습니다`
                                : '등록된 이용권이 없습니다'}
                        </Body2Regular>
                    </Col>
                ) : viewMode === 'detail' ? (
                    <Grid
                        key={dateBase}
                        colDefs={colDefs}
                        rowData={subscriptions}
                        leftTopCustomComponent={() => (
                            <CaptionLight color={colors.LAVEL_1}>
                                {subscriptions.length < totalResults
                                    ? `이용권 ${subscriptions.length.toLocaleString()}건 표시 / 필터 결과 ${totalResults.toLocaleString()}건`
                                    : `이용권 총 ${totalResults.toLocaleString()}건`}
                            </CaptionLight>
                        )}
                        centerTopComponent="pagination"
                        onClickRow={(row) => setEditTarget(row as IFacilitySubscription)}
                    />
                ) : (
                    /* 매출 요약 뷰 — 상품별 집계 */
                    <Col style={{ padding: '20px 24px' }}>
                        <Row className="items-center justify-between mb-[16px]" style={{ flexWrap: 'wrap', gap: 12 }}>
                            <CaptionLight color={colors.LAVEL_1}>
                                {dateBase === 'payment' ? '결제일' : dateBase === 'end' ? '종료일' : '시작일'} 기준 · 필터 결과 {revenue.count.toLocaleString()}건 · 상품 {revenue.products.length}개
                            </CaptionLight>
                            <Row style={{ gap: 16, alignItems: 'center' }}>
                                <span style={{ fontSize: 12, color: '#6B7280' }}>
                                    총매출 <b style={{ color: '#1F2937', marginLeft: 4 }}>{revenue.gross.toLocaleString()}원</b>
                                </span>
                                <span style={{ fontSize: 12, color: '#6B7280' }}>
                                    − 환불 <b style={{ color: '#DC2626', marginLeft: 4 }}>{revenue.refundTotal.toLocaleString()}원</b> ({revenue.refundCount}건)
                                </span>
                                <span style={{ fontSize: 13, color: '#1E40AF', fontWeight: 700 }}>= 순매출 {revenue.net.toLocaleString()}원</span>
                            </Row>
                        </Row>
                        {revenue.products.length === 0 ? (
                            <Col className="items-center py-[40px] gap-[8px]">
                                <span className="text-[36px]">💸</span>
                                <Body2Regular color={colors.LAVEL_2}>해당 기간 매출이 없습니다</Body2Regular>
                            </Col>
                        ) : (
                            <div
                                style={{
                                    border: '1px solid #E5E7EB',
                                    borderRadius: 8,
                                    overflow: 'hidden',
                                }}
                            >
                                <table
                                    style={{
                                        width: '100%',
                                        borderCollapse: 'collapse',
                                        fontSize: 14,
                                    }}
                                >
                                    <thead>
                                        <tr style={{ backgroundColor: '#F9FAFB' }}>
                                            <th
                                                style={{
                                                    padding: '12px 16px',
                                                    textAlign: 'left',
                                                    fontWeight: 600,
                                                    color: '#6B7280',
                                                    fontSize: 13,
                                                }}
                                            >
                                                상품명
                                            </th>
                                            <th
                                                style={{
                                                    padding: '12px 16px',
                                                    textAlign: 'right',
                                                    fontWeight: 600,
                                                    color: '#6B7280',
                                                    fontSize: 13,
                                                }}
                                            >
                                                판매 수
                                            </th>
                                            <th
                                                style={{
                                                    padding: '12px 16px',
                                                    textAlign: 'right',
                                                    fontWeight: 600,
                                                    color: '#6B7280',
                                                    fontSize: 13,
                                                }}
                                            >
                                                총 매출
                                            </th>
                                            <th
                                                style={{
                                                    padding: '12px 16px',
                                                    textAlign: 'right',
                                                    fontWeight: 600,
                                                    color: '#6B7280',
                                                    fontSize: 13,
                                                }}
                                            >
                                                환불
                                            </th>
                                            <th
                                                style={{
                                                    padding: '12px 16px',
                                                    textAlign: 'right',
                                                    fontWeight: 600,
                                                    color: '#6B7280',
                                                    fontSize: 13,
                                                }}
                                            >
                                                순매출
                                            </th>
                                            <th
                                                style={{
                                                    padding: '12px 16px',
                                                    textAlign: 'right',
                                                    fontWeight: 600,
                                                    color: '#6B7280',
                                                    fontSize: 13,
                                                }}
                                            >
                                                비중
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {revenue.products.map((p) => {
                                            const pct = revenue.net > 0 ? (p.net / revenue.net) * 100 : 0;
                                            return (
                                                <tr key={p.name} style={{ borderTop: '1px solid #F3F4F6' }}>
                                                    <td
                                                        style={{
                                                            padding: '12px 16px',
                                                            color: '#1F2937',
                                                            fontWeight: 500,
                                                        }}
                                                    >
                                                        {p.name}
                                                    </td>
                                                    <td
                                                        style={{
                                                            padding: '12px 16px',
                                                            textAlign: 'right',
                                                            color: '#374151',
                                                        }}
                                                    >
                                                        {p.count}건
                                                    </td>
                                                    <td
                                                        style={{
                                                            padding: '12px 16px',
                                                            textAlign: 'right',
                                                            color: '#374151',
                                                        }}
                                                    >
                                                        {p.gross.toLocaleString()}원
                                                    </td>
                                                    <td
                                                        style={{
                                                            padding: '12px 16px',
                                                            textAlign: 'right',
                                                            color: p.refund > 0 ? '#DC2626' : '#D1D5DB',
                                                        }}
                                                    >
                                                        {p.refund > 0 ? `-${p.refund.toLocaleString()}원` : '-'}
                                                        {p.refundCount > 0 && (
                                                            <span
                                                                style={{
                                                                    fontSize: 11,
                                                                    color: '#9CA3AF',
                                                                    marginLeft: 4,
                                                                }}
                                                            >
                                                                ({p.refundCount}건)
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td
                                                        style={{
                                                            padding: '12px 16px',
                                                            textAlign: 'right',
                                                            color: '#1E40AF',
                                                            fontWeight: 700,
                                                        }}
                                                    >
                                                        {p.net.toLocaleString()}원
                                                    </td>
                                                    <td
                                                        style={{
                                                            padding: '12px 16px',
                                                            textAlign: 'right',
                                                            color: '#6B7280',
                                                            fontSize: 12,
                                                        }}
                                                    >
                                                        {pct.toFixed(1)}%
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                    <tfoot>
                                        <tr
                                            style={{
                                                borderTop: '2px solid #E5E7EB',
                                                backgroundColor: '#F9FAFB',
                                            }}
                                        >
                                            <td
                                                style={{
                                                    padding: '12px 16px',
                                                    fontWeight: 700,
                                                    color: '#1F2937',
                                                }}
                                            >
                                                합계
                                            </td>
                                            <td
                                                style={{
                                                    padding: '12px 16px',
                                                    textAlign: 'right',
                                                    fontWeight: 700,
                                                    color: '#1F2937',
                                                }}
                                            >
                                                {/* 판매 수 합계는 양도 사본을 뺀 값(서버 salesCount, 구서버면 count). 목록 건수는 count 그대로. */}
                                                {((revenue as { salesCount?: number }).salesCount ?? revenue.count).toLocaleString()}건
                                            </td>
                                            <td
                                                style={{
                                                    padding: '12px 16px',
                                                    textAlign: 'right',
                                                    fontWeight: 700,
                                                    color: '#1F2937',
                                                }}
                                            >
                                                {revenue.gross.toLocaleString()}원
                                            </td>
                                            <td
                                                style={{
                                                    padding: '12px 16px',
                                                    textAlign: 'right',
                                                    fontWeight: 700,
                                                    color: revenue.refundTotal > 0 ? '#DC2626' : '#D1D5DB',
                                                }}
                                            >
                                                {revenue.refundTotal > 0 ? `-${revenue.refundTotal.toLocaleString()}원` : '-'}
                                            </td>
                                            <td
                                                style={{
                                                    padding: '12px 16px',
                                                    textAlign: 'right',
                                                    fontWeight: 800,
                                                    color: '#1E40AF',
                                                }}
                                            >
                                                {revenue.net.toLocaleString()}원
                                            </td>
                                            <td
                                                style={{
                                                    padding: '12px 16px',
                                                    textAlign: 'right',
                                                    color: '#6B7280',
                                                }}
                                            >
                                                100%
                                            </td>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>
                        )}
                    </Col>
                )}
            </Col>

            <div style={{ height: 48 }} />

            {editTarget && (
                <SubscriptionEditModal
                    isVisible={!!editTarget}
                    onClose={() => setEditTarget(null)}
                    centerId={centerId}
                    memberId={editTarget.memberId}
                    subscription={editTarget}
                    createAlert={createAlert}
                    onTransfer={
                        isCenterViewer
                            ? undefined
                            : () => {
                                  setTransferTarget(editTarget);
                                  setEditTarget(null);
                              }
                    }
                />
            )}

            {transferTarget && !isCenterViewer && (
                <SubscriptionTransferModal
                    centerId={centerId}
                    subscription={transferTarget}
                    sourceMemberName={transferTarget.memberName || ''}
                    onClose={() => setTransferTarget(null)}
                    onDone={(result, recipientName) => {
                        setTransferTarget(null);
                        createAlert('양도 완료', buildTransferDoneMessage(result, recipientName), false);
                    }}
                />
            )}

            {/* 사업자 정보 (카드사 심사용) */}
            <div style={{ width: '100%', marginTop: 40 }}>
                <BusinessInfoFooter />
            </div>
        </Col>
    );
};

export default SubscriptionListPage;
